import type {
  SubmitData,
  SubmitActions,
  PaymentAction,
  Core,
  UIElement,
  CardConfiguration,
  PaymentFailedData,
  ResultCode,
  AdyenCheckoutError,
} from '@adyen/adyen-web';
import { ADYEN_CARD_CONFIG, ADYEN_CARD_FIELD, ADYEN_DATE_FIELD } from '@/constants/adyen';
import { createAdyenConfiguration, resolve3DSChallengeWindowSize } from '@/utils/adyen';
import type {
  AdyenConfig,
  AdyenCardComponent,
  CardDetails,
  CardFieldEvent,
  BinValueEvent,
  CallbackActions,
} from '@/types/adyen';
import type { Customer } from '@/types/checkout';
import type { AppchargeErrorType } from '@/types/payment';
import { logError } from '@/utils/logger';

type PaymentErrorHandler = (appChargeErrorType?: AppchargeErrorType, rawReason?: string) => void;

class AdyenService {
  private checkoutInstance: Core | null = null;
  private isInitializing = false;
  private onPaymentSuccess: (params: unknown) => void = () => {};
  private onPaymentError: PaymentErrorHandler = () => {};
  private onPaymentFailed: () => void = () => {};
  private pendingFailureReason?: string;

  setOnPaymentFailed(onPaymentFailed: () => void): void {
    this.onPaymentFailed = onPaymentFailed;
  }

  runPaymentFailed(): void {
    this.onPaymentFailed?.();
  }

  updateOnPaymentSuccess(onPaymentSuccess: (params: unknown) => void): void {
    this.onPaymentSuccess = onPaymentSuccess;
  }

  updateOnPaymentError(onPaymentError: PaymentErrorHandler): void {
    this.onPaymentError = onPaymentError;
  }

  callOnPaymentSuccess(result: unknown): void {
    this.pendingFailureReason = undefined;
    this.onPaymentSuccess(result);
  }

  // A card challenge hands its failure back through the SDK's SubmitActions so
  // the Card component's `onPaymentFailed` runs (the only path that resets the
  // form to 'ready'). `actions.reject()` carries no payload, so the reason is
  // stashed here for `onPaymentFailed` to pick up — without it the card path,
  // which is ~79% of 3DS completions, reports no reason at all.
  setPendingFailureReason(rawReason?: string): void {
    this.pendingFailureReason = rawReason;
  }

  // `rawReason` carries the failure detail discovered at challenge-time (the
  // `/status` resultCode, or an SDK component error). Without it the handler
  // falls back to the pre-challenge response, which is a `requires_action`
  // payload and never holds a decline reason.
  callOnPaymentError(appChargeErrorType?: AppchargeErrorType, rawReason?: string): void {
    this.onPaymentError(appChargeErrorType, rawReason);
  }

  async initialize(config: AdyenConfig, customer?: Customer): Promise<Core> {
    if (this.checkoutInstance) {
      return this.checkoutInstance;
    }

    if (this.isInitializing) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.initialize(config, customer);
    }

    this.isInitializing = true;

    try {
      const configuration = createAdyenConfiguration(config, customer);

      if (!configuration.environment) {
        throw new Error('Adyen environment is required (test or live)');
      }

      const adyenWeb = await import('@adyen/adyen-web');
      const AdyenCheckoutClass = adyenWeb.AdyenCheckout || (adyenWeb as Record<string, unknown>).default;

      if (!AdyenCheckoutClass || typeof AdyenCheckoutClass !== 'function') {
        throw new Error('Failed to import AdyenCheckout');
      }

      const instance = await AdyenCheckoutClass(configuration);
      this.checkoutInstance = instance;
      return this.checkoutInstance;
    } finally {
      this.isInitializing = false;
    }
  }

  async createFromAction(action: PaymentAction, callbacks: CallbackActions): Promise<unknown> {
    try {
      return (
        this.checkoutInstance &&
        this.checkoutInstance.createFromAction(action, {
          ...ADYEN_CARD_CONFIG,
          challengeWindowSize: resolve3DSChallengeWindowSize(),
          ...callbacks,
        })
      );
    } catch (error) {
      logError('Failed to create from action', error);
      return null;
    }
  }

  async createCardComponent(
    container: HTMLElement,
    handlers: {
      onSubmit: (state: SubmitData, component: unknown, actions: SubmitActions) => void;
      onError: (error: AdyenCheckoutError) => void;
      onFieldValid: (event: CardFieldEvent) => void;
      onBinValue: (event: BinValueEvent) => void;
      onConfigSuccess: () => void;
    },
    configOverrides: Partial<CardConfiguration> = {}
  ): Promise<AdyenCardComponent | null> {
    if (!this.checkoutInstance) return null;

    try {
      const { Card } = await import('@adyen/adyen-web');

      const component = new Card(this.checkoutInstance as unknown as never, {
        ...ADYEN_CARD_CONFIG,
        ...configOverrides,
        ...handlers,
        onPaymentCompleted: () => {
          this.pendingFailureReason = undefined;
          this.onPaymentSuccess(null);
        },
        onPaymentFailed: (data, component: UIElement) => {
          component?.setStatus('ready');
          this.onPaymentError(undefined, this.takeFailureReason(data));
        },
      });
      component.mount(container);
      return component as unknown as AdyenCardComponent;
    } catch (error) {
      logError('Failed to create card component', error);
      return null;
    }
  }

  // Resolving the SubmitActions with a failed resultCode makes the SDK reject
  // internally with the response itself, so `data` carries Refused / Cancelled
  // / Error. An explicit `actions.reject()` carries nothing, which is what the
  // stashed reason covers. Consuming it here keeps a reason that was never
  // used from being mis-attributed to a later, unrelated failure.
  private takeFailureReason(data?: PaymentFailedData): string | undefined {
    const reason = this.pendingFailureReason ?? (data as { resultCode?: ResultCode } | undefined)?.resultCode;
    this.pendingFailureReason = undefined;
    return reason;
  }

  reset(): void {
    this.checkoutInstance = null;
    this.isInitializing = false;
  }
}

export const adyenService = new AdyenService();

export const createCardFieldHandlers = (
  cardDetailsRef: { current: CardDetails },
  _: { current: AdyenCardComponent | undefined },
  onError: (error: AdyenCheckoutError) => void
) => ({
  onError,
  onFieldValid: (event: CardFieldEvent) => {
    const { fieldType, valid, endDigits, expiryDate } = event;
    if (fieldType === ADYEN_CARD_FIELD && valid && endDigits) {
      cardDetailsRef.current.endDigits = endDigits;
    } else if (fieldType === ADYEN_DATE_FIELD && valid && expiryDate) {
      cardDetailsRef.current.expiryDate = expiryDate;
    }
  },
  onBinValue: (event: BinValueEvent) => {
    const { binValue, brand } = event;
    if (binValue) {
      cardDetailsRef.current.bin = binValue;
    }
    if (brand) {
      cardDetailsRef.current.cardType = brand;
    }
  },
});
